import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ModeratorBadge } from "../components/ModeratorBadge";
import { UserName } from "../components/UserName";
import Avatar from "../components/Avatar";
import { Copy, Check, Crown, Shield, LogOut, MessageSquare, Play, Send, Settings as SettingsIcon, UserPlus, Users, X, Swords, Zap } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { useLobbySocket } from "../hooks/useLobbySocket";
import { toast } from "sonner";
import LobbyInviteFriendsModal from "../components/LobbyInviteFriendsModal";

function StatusPill({ status }) {
  const map = {
    waiting: ["bg-[var(--accent)]/10 text-[var(--accent)] border-[var(--accent)]/30", "Waiting"],
    active: ["bg-amber-400/10 text-amber-300 border-amber-400/30", "In game"],
    finished: ["bg-zinc-500/10 text-zinc-300 border-zinc-500/30", "Finished"],
    closed: ["bg-red-500/10 text-red-300 border-red-500/30", "Closed"],
  };
  const [cls, label] = map[status] || map.waiting;
  return (
    <span
      data-testid="lobby-status"
      className={`text-[10px] uppercase tracking-widest px-3 py-1 rounded-full border ${cls}`}
    >
      {label}
    </span>
  );
}

function ConnectionDot({ status }) {
  const map = {
    open: ["bg-[var(--accent)]", "Live"],
    connecting: ["bg-amber-400 animate-pulse", "Connecting…"],
    reconnecting: ["bg-amber-400 animate-pulse", "Reconnecting…"],
    closed: ["bg-red-500", "Disconnected"],
  };
  const [cls, label] = map[status] || map.connecting;
  return (
    <span
      data-testid="lobby-ws-status"
      className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-400"
    >
      <span className={`w-2 h-2 rounded-full ${cls}`} />
      {label}
    </span>
  );
}

function SettingsView({ lobby, isHost, onChange }) {
  if (lobby.is_ranked) {
    const s = lobby.settings || {};
    return (
      <div
        data-testid="lobby-ranked-settings-banner"
        className="rounded-2xl border border-emerald-400/40 bg-emerald-400/5 p-5 flex items-center gap-4"
      >
        <Swords size={22} className="text-emerald-300 shrink-0" />
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 font-bold">Ranked · server-managed</div>
          <div className="mt-0.5 text-lg font-black capitalize">
            {(lobby.ranked_mode || s.game_mode || "classic").replace("_", " ")}
            {s.game_mode !== "quiz" && s.difficulty ? ` · ${s.difficulty}` : ""}
          </div>
          <div className="text-[11px] text-zinc-400 mt-0.5">
            {s.rounds_count} rounds · {s.time_per_round_sec}s · settings locked, kick disabled, auto-start
          </div>
        </div>
        <Zap size={18} className="text-emerald-300" />
      </div>
    );
  }
  const s = lobby.settings;
  const editable = isHost && lobby.status === "waiting";
  return (
    <div className="rounded-2xl border border-white/10 bg-[var(--surface)] p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold flex items-center gap-2">
          <SettingsIcon size={14} className="text-[var(--accent)]" /> Settings
        </h3>
        <span className="text-[10px] uppercase tracking-widest text-zinc-500">
          {editable ? "you can edit" : "host-controlled"}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
        <div className="col-span-2 sm:col-span-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Mode</div>
          {editable ? (
            <div className="mt-1 grid grid-cols-3 gap-2" role="radiogroup" aria-label="Game mode">
              {[
                { value: "classic", label: "Classic" },
                { value: "career_path", label: "Career Path" },
                { value: "quiz", label: "Quiz" },
              ].map((m) => (
                <button
                  key={m.value}
                  type="button"
                  role="radio"
                  aria-checked={(s.game_mode || "classic") === m.value}
                  onClick={() => onChange({ game_mode: m.value })}
                  data-testid={`settings-mode-${m.value}`}
                  className={`px-3 py-2 rounded-md border text-xs font-semibold transition ${
                    (s.game_mode || "classic") === m.value
                      ? "bg-[var(--accent)] text-black border-[var(--accent)]"
                      : "border-white/10 text-zinc-300 hover:bg-white/5"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          ) : (
            <div className="mt-1 capitalize font-semibold">
              {(s.game_mode || "classic").replace("_", " ")}
            </div>
          )}
        </div>
        {s.game_mode !== "quiz" && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Difficulty</div>
            {editable ? (
              <select
                data-testid="settings-difficulty"
                value={s.difficulty || "normal"}
                onChange={(e) => onChange({ difficulty: e.target.value })}
                className="mt-1 w-full bg-black border border-white/10 rounded-md px-2 py-1.5 focus:border-[var(--accent)] outline-none"
              >
                <option value="normal">Normal</option>
                <option value="hard">Hard</option>
                <option value="insane">Insane</option>
              </select>
            ) : (
              <div className="mt-1 capitalize font-semibold">{s.difficulty}</div>
            )}
          </div>
        )}
        {s.game_mode === "quiz" && (
          <div data-testid="settings-quiz-mixed-pool-note"
               className="col-span-2 sm:col-span-3 rounded-md border border-white/10 bg-white/[0.02] px-3 py-2 text-[11px] text-zinc-400">
            Quiz mode draws random questions across all difficulties — no setting needed.
          </div>
        )}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Rounds</div>
          {editable ? (
            <select
              data-testid="settings-rounds"
              value={s.rounds_count}
              onChange={(e) => onChange({ rounds_count: Number(e.target.value) })}
              className="mt-1 w-full bg-black border border-white/10 rounded-md px-2 py-1.5 focus:border-[var(--accent)] outline-none"
            >
              {[3, 5, 10, 15, 20].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          ) : (
            <div className="mt-1 font-semibold">{s.rounds_count}</div>
          )}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Sec / round</div>
          {editable ? (
            <div className="mt-1 grid grid-cols-3 gap-1" role="radiogroup" aria-label="Seconds per round">
              {[30, 45, 60].map((v) => (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={s.time_per_round_sec === v}
                  onClick={() => onChange({ time_per_round_sec: v })}
                  data-testid={`settings-time-${v}`}
                  className={`px-2 py-1.5 rounded-md border text-xs transition font-mono ${
                    s.time_per_round_sec === v
                      ? "bg-[var(--accent)] text-black border-[var(--accent)] font-bold"
                      : "border-white/10 text-zinc-300 hover:bg-white/5"
                  }`}
                >
                  {v}s
                </button>
              ))}
            </div>
          ) : (
            <div className="mt-1 font-semibold">{s.time_per_round_sec}s</div>
          )}
        </div>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            data-testid="settings-include-clubs"
            type="checkbox"
            disabled={!editable}
            checked={s.include_clubs}
            onChange={(e) => onChange({ include_clubs: e.target.checked })}
            className="accent-[var(--accent)]"
          />
          <span className="text-sm">Clubs</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            data-testid="settings-include-nt"
            type="checkbox"
            disabled={!editable}
            checked={s.include_national_teams}
            onChange={(e) => onChange({ include_national_teams: e.target.checked })}
            className="accent-[var(--accent)]"
          />
          <span className="text-sm">National teams</span>
        </label>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Max players</div>
          <div className="mt-1 font-semibold">{lobby.max_players}</div>
        </div>
      </div>
    </div>
  );
}

function PlayerRow({ p, isHostView, currentUserId, onKick, index, isRanked }) {
  const me = p.user_id === currentUserId;
  const initial = (p.username || "?")[0]?.toUpperCase();
  const dot = p.is_connected ? "bg-[var(--accent)]" : "bg-zinc-500";
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
      data-testid={`lobby-player-${p.user_id}`}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${
        me
          ? "border-[var(--accent)]/40 bg-[var(--accent)]/8"
          : "border-white/5 bg-[var(--surface-2)]/40"
      }`}
    >
      <div className="relative shrink-0">
        <Avatar user={p} size={44} />
        <span
          className={`absolute -bottom-0.5 -left-0.5 w-3 h-3 rounded-full border-2 border-[var(--bg)] ${dot}`}
          aria-label={p.is_connected ? "online" : "offline"}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold truncate flex items-center gap-1.5"><UserName user={p} /></span>
          {me && (
            <span className="text-[9px] text-zinc-500 uppercase tracking-widest">(you)</span>
          )}
          {p.is_host && !isRanked && (
            <span className="text-[9px] text-amber-300 uppercase tracking-widest inline-flex items-center gap-1 font-bold">
              <Crown size={10} /> host
            </span>
          )}
          {isRanked && (
            <span className="text-[9px] text-emerald-300 uppercase tracking-widest inline-flex items-center gap-1 font-bold">
              <Shield size={10} /> contender
            </span>
          )}
        </div>
        <div className="text-[11px] mt-0.5">
          {p.is_ready ? (
            <span className="text-[var(--accent)] inline-flex items-center gap-1 font-bold">
              <Check size={10} /> ready
            </span>
          ) : (
            <span className="text-zinc-500">not ready</span>
          )}
        </div>
      </div>
      {isHostView && !me && (
        <button
          onClick={() => onKick(p.user_id)}
          data-testid={`lobby-kick-${p.user_id}`}
          className="text-[10px] uppercase tracking-widest text-red-400 hover:text-red-200 px-2 py-1 rounded-md hover:bg-red-500/10"
        >
          kick
        </button>
      )}
    </motion.div>
  );
}

function ChatPanel({ messages, onSend, currentUserId }) {
  const [text, setText] = useState("");
  const listRef = useRef(null);
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);
  const submit = (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t) return;
    onSend(t);
    setText("");
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-[var(--surface)] p-4 flex flex-col h-80">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
        <MessageSquare size={11} /> Chat
      </div>
      <div
        ref={listRef}
        data-testid="chat-list"
        className="flex-1 overflow-y-auto space-y-1 text-sm pr-1"
      >
        {messages.length === 0 && (
          <div className="text-zinc-600 text-xs italic">Say hi to your opponents…</div>
        )}
        {messages.map((m, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: 4 }}
            animate={{ opacity: 1, x: 0 }}
            className="leading-snug"
          >
            <span
              className={`font-semibold inline-flex items-center gap-1 ${
                m.user_id === currentUserId ? "text-[var(--accent)]" : "text-zinc-200"
              }`}
            >
              <UserName user={m} hideModBadge />:
            </span>{" "}
            <span className="text-zinc-300">{m.text}</span>
          </motion.div>
        ))}
      </div>
      <form onSubmit={submit} className="mt-2 flex gap-2">
        <input
          data-testid="chat-input"
          type="text"
          maxLength={200}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="message…"
          className="flex-1 px-3 py-2 rounded-lg bg-black border border-white/10 focus:border-[var(--accent)] outline-none text-sm"
        />
        <button
          type="submit"
          data-testid="chat-send"
          aria-label="Send"
          className="px-3 py-2 rounded-lg bg-[var(--accent)] text-black font-semibold text-sm inline-flex items-center gap-1.5 hover:brightness-110 transition"
        >
          <Send size={14} />
        </button>
      </form>
    </div>
  );
}

function RankedCountdownOverlay({ targetIso, onDecline, busy }) {
  const [secs, setSecs] = useState(5);
  useEffect(() => {
    const target = targetIso ? new Date(targetIso).getTime() : Date.now() + 5000;
    const tick = () => {
      const left = Math.max(0, Math.ceil((target - Date.now()) / 1000));
      setSecs(left);
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [targetIso]);
  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="ranked-countdown-overlay"
      className="rounded-2xl border-2 border-emerald-400/60 bg-gradient-to-r from-emerald-500/15 to-emerald-900/40 px-6 py-5 flex items-center gap-5 shadow-[0_0_30px_-5px_var(--accent)]"
    >
      <motion.div
        key={secs}
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="text-5xl font-black text-emerald-300 tabular-nums w-16 text-center"
        data-testid="ranked-countdown-seconds"
      >
        {secs}
      </motion.div>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-[0.4em] text-emerald-300 font-bold">match starting</div>
        <div className="text-2xl font-black">Get ready · auto-starting in {secs}s</div>
        <div className="text-xs text-zinc-400 mt-0.5">All players confirmed. Difficulty + rules locked.</div>
      </div>
      <button
        type="button"
        onClick={onDecline}
        disabled={busy}
        data-testid="ranked-decline-btn"
        className="px-4 py-2 rounded-full border border-red-400/50 text-red-200 hover:bg-red-500/10 text-xs uppercase tracking-widest disabled:opacity-50"
      >
        Decline
      </button>
    </motion.div>
  );
}

export default function Lobby() {
  const { code } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [lobby, setLobby] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [chat, setChat] = useState([]);
  const [copied, setCopied] = useState(false);
  const [inviteFriendsOpen, setInviteFriendsOpen] = useState(false);
  const navigatedToGameRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/lobbies/${code}`);
        if (!cancelled) setLobby(data);
      } catch (e) {
        if (cancelled) return;
        const s = e?.response?.status;
        if (s === 404) {
          toast.error("Lobby not found");
          navigate("/dashboard", { replace: true });
        } else if (s === 401) {
          navigate("/login", { replace: true });
        } else {
          setErr(e?.response?.data?.detail || "Failed to load lobby");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, navigate]);

  const handleEvent = useCallback(
    (evt) => {
      switch (evt.type) {
        case "lobby_state": {
          const s = evt.payload;
          setLobby((prev) => ({ ...(prev || {}), ...s }));
          if (s.status === "active" && !navigatedToGameRef.current) {
            navigatedToGameRef.current = true;
            navigate(`/lobby/${code}/game`, { replace: true });
          }
          break;
        }
        case "game_started":
          if (!navigatedToGameRef.current) {
            navigatedToGameRef.current = true;
            navigate(`/lobby/${code}/game`, { replace: true });
          }
          break;
        case "chat_message":
          setChat((prev) => [...prev.slice(-99), evt.payload]);
          break;
        case "player_kicked":
          if (evt.payload?.user_id === user?.id) {
            toast.error("You were removed from this lobby");
            navigate("/dashboard", { replace: true });
          }
          break;
        case "lobby_disbanded": {
          const host = evt.payload?.host_username || "Host";
          toast.error(`Lobby was disbanded by ${host}`);
          navigate("/dashboard", { replace: true });
          break;
        }
        case "error":
          if (evt.payload?.code === "kicked") {
            navigate("/dashboard", { replace: true });
          } else if (evt.payload?.message) {
            toast.error(evt.payload.message);
          }
          break;
        default:
          break;
      }
    },
    [code, navigate, user?.id]
  );

  const { status: wsStatus, send } = useLobbySocket(code, handleEvent);

  const isHost = lobby && lobby.host_user_id === user?.id;
  const me = useMemo(
    () => lobby?.players?.find((p) => p.user_id === user?.id) || null,
    [lobby, user?.id]
  );

  if (err && !lobby) {
    return (
      <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] flex items-center justify-center px-6">
        <div className="text-center">
          <div className="text-3xl font-bold mb-2">Lobby unavailable</div>
          <div className="text-sm text-zinc-400">{err}</div>
          <Link
            to="/dashboard"
            className="mt-6 inline-block px-5 py-2.5 rounded-full bg-[var(--accent)] text-black font-semibold"
          >
            Back to dashboard
          </Link>
        </div>
      </div>
    );
  }
  if (!lobby) {
    return (
      <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] flex items-center justify-center text-xs uppercase tracking-[0.4em]">
        loading lobby…
      </div>
    );
  }

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(lobby.invite_url);
      setCopied(true);
      toast.success("Invite link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed — link: " + lobby.invite_url);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(lobby.lobby_code);
      setCopied(true);
      toast.success("Lobby code copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* ignore */
    }
  };

  const onLeave = async () => {
    setBusy(true);
    try {
      await api.post(`/lobbies/${code}/leave`);
      navigate("/dashboard", { replace: true });
    } finally {
      setBusy(false);
    }
  };

  const onDisband = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Disband lobby ${code}? All players will be returned to dashboard.`)) return;
    setBusy(true);
    try {
      await api.post(`/lobbies/${code}/disband`);
      toast.success("Lobby disbanded");
      navigate("/dashboard", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not disband");
    } finally {
      setBusy(false);
    }
  };

  const onReady = () => {
    if (!me) return;
    send("ready", { is_ready: !me.is_ready });
  };

  const onStart = async () => {
    setBusy(true);
    try {
      await api.post(`/lobbies/${code}/start`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start");
    } finally {
      setBusy(false);
    }
  };

  const onSettingsChange = (patch) => send("update_settings", patch);
  const onKick = (uid) => send("kick", { user_id: uid });
  const onChatSend = (text) => send("chat_message", { text });

  const onDeclineRanked = async () => {
    setBusy(true);
    try {
      await api.post(`/lobbies/${code}/leave`);
      toast.message("Match declined", { description: "Disconnect penalty may apply." });
      navigate("/ranked", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not decline");
    } finally {
      setBusy(false);
    }
  };

  const playerCount = lobby.players?.length || 0;
  const allNonHostReady = (lobby.players || [])
    .filter((p) => !p.is_host)
    .every((p) => p.is_ready);
  const canStart =
    isHost && lobby.status === "waiting" && playerCount >= 2 && allNonHostReady;
  const startTooltip = !isHost
    ? "Only the host can start"
    : playerCount < 2
    ? "Need at least 2 players"
    : !allNonHostReady
    ? "Waiting for everyone to ready up"
    : "";

  return (
    <div data-testid="lobby-page" className="min-h-screen bg-[var(--bg)] text-[var(--text)] relative">
      <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />

      <header className="relative px-6 py-5 max-w-5xl mx-auto flex items-center justify-between">
        <Link
          to="/dashboard"
          className="text-xs uppercase tracking-[0.4em] text-[var(--accent)] hover:brightness-125 transition"
        >
          ← dashboard
        </Link>
        <div className="flex items-center gap-4">
          {lobby.is_ranked && (
            <span
              data-testid="lobby-ranked-badge"
              className="text-[10px] uppercase tracking-[0.3em] px-3 py-1 rounded-full border border-emerald-400/60 bg-emerald-400/10 text-emerald-300 font-bold inline-flex items-center gap-1.5"
            >
              <Swords size={11} /> RANKED
            </span>
          )}
          <ConnectionDot status={wsStatus} />
          <StatusPill status={lobby.status} />
        </div>
      </header>

      <main className="relative max-w-5xl mx-auto px-6 pb-16 space-y-8">
        {/* HERO — lobby code + actions */}
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col lg:flex-row lg:items-end justify-between gap-6"
        >
          <div>
            <div className="text-xs uppercase tracking-[0.4em] text-[var(--accent)]">lobby code</div>
            <button
              type="button"
              onClick={copyCode}
              data-testid="lobby-code"
              className="text-6xl sm:text-7xl font-black tracking-[0.2em] mt-2 font-mono text-[var(--text)] hover:text-[var(--accent)] transition select-all relative group"
            >
              {lobby.lobby_code}
              <span className="absolute -right-8 top-4 opacity-0 group-hover:opacity-100 transition text-[var(--accent)]">
                <Copy size={18} />
              </span>
            </button>
            <div className="mt-4 flex flex-wrap items-center gap-2.5">
              <button
                data-testid="lobby-copy-invite"
                onClick={copyInvite}
                className="px-4 py-2 rounded-full border border-white/15 text-sm hover:bg-white/5 transition inline-flex items-center gap-2"
              >
                <AnimatePresence mode="wait">
                  {copied ? (
                    <motion.span
                      key="ok"
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.5, opacity: 0 }}
                      className="inline-flex items-center gap-1.5 text-[var(--accent)] pop-check"
                    >
                      <Check size={14} /> Copied
                    </motion.span>
                  ) : (
                    <motion.span key="cp" className="inline-flex items-center gap-1.5">
                      <Copy size={14} /> Copy invite link
                    </motion.span>
                  )}
                </AnimatePresence>
              </button>
              <button
                data-testid="open-invite-friends-modal"
                onClick={() => setInviteFriendsOpen(true)}
                className="px-4 py-2 rounded-full border border-white/15 text-zinc-200 hover:bg-white/5 inline-flex items-center gap-2 text-sm transition"
              >
                <UserPlus size={14} /> Invite friends
              </button>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1">
                <Users size={12} /> {playerCount}/{lobby.max_players}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {!isHost && me && !lobby.is_ranked && (
              <motion.button
                whileTap={{ scale: 0.96 }}
                onClick={onReady}
                data-testid="lobby-ready"
                className={`px-5 py-2.5 rounded-full font-semibold transition inline-flex items-center gap-2 ${
                  me.is_ready
                    ? "bg-[var(--surface-2)] text-zinc-300 hover:bg-zinc-800 border border-white/10"
                    : "bg-[var(--accent)] text-black hover:brightness-110 shadow-[0_8px_30px_-12px_var(--accent)]"
                }`}
              >
                {me.is_ready ? (
                  <>
                    <X size={14} /> Unready
                  </>
                ) : (
                  <>
                    <Check size={14} /> I'm ready
                  </>
                )}
              </motion.button>
            )}
            {isHost && (
              <motion.button
                whileTap={{ scale: 0.96 }}
                onClick={onStart}
                disabled={!canStart || busy}
                title={startTooltip}
                data-testid="lobby-start"
                className="px-5 py-2.5 rounded-full bg-[var(--accent)] text-black font-bold hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition inline-flex items-center gap-2 shadow-[0_8px_30px_-12px_var(--accent)]"
              >
                <Play size={14} strokeWidth={3} /> Start game
              </motion.button>
            )}
            {isHost && !lobby.is_ranked ? (
              <button
                onClick={onDisband}
                disabled={busy}
                data-testid="lobby-terminate"
                title="Disbands the lobby for everyone"
                className="px-5 py-2.5 rounded-full border border-red-500/40 text-sm text-red-300 hover:bg-red-500/10 transition inline-flex items-center gap-2"
              >
                <LogOut size={14} /> Terminate Lobby
              </button>
            ) : (
              <button
                onClick={onLeave}
                disabled={busy}
                data-testid="lobby-leave"
                className="px-5 py-2.5 rounded-full border border-white/15 text-sm hover:bg-white/5 transition inline-flex items-center gap-2"
              >
                <LogOut size={14} /> Leave
              </button>
            )}
          </div>
        </motion.section>

        {lobby.is_ranked && lobby.status === "waiting" && (
          <RankedCountdownOverlay
            targetIso={lobby.auto_start_at}
            onDecline={onDeclineRanked}
            busy={busy}
          />
        )}

        <SettingsView lobby={lobby} isHost={isHost} onChange={onSettingsChange} />

        <section className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Users size={14} className="text-[var(--accent)]" /> Players{" "}
                <span className="text-zinc-500 text-sm font-normal">
                  ({playerCount}/{lobby.max_players})
                </span>
              </h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {lobby.players?.map((p, i) => (
                <PlayerRow
                  key={p.user_id}
                  p={p}
                  isHostView={isHost}
                  currentUserId={user?.id}
                  onKick={onKick}
                  index={i}
                  isRanked={lobby.is_ranked}
                />
              ))}
              {playerCount < lobby.max_players &&
                Array.from({ length: lobby.max_players - playerCount }).map((_, i) => (
                  <motion.div
                    key={`empty-${i}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.05 * (playerCount + i) }}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl border border-dashed border-white/10 bg-[var(--surface)]/30 text-zinc-600"
                  >
                    <div className="w-11 h-11 rounded-full border border-dashed border-white/10" />
                    <div className="text-sm italic">empty slot</div>
                  </motion.div>
                ))}
            </div>
          </div>
          <ChatPanel messages={chat} onSend={onChatSend} currentUserId={user?.id}
                     playersById={Object.fromEntries((lobby?.players || []).map((p) => [p.user_id, p]))} />
        </section>

        {startTooltip && isHost && !lobby.is_ranked && (
          <div
            className="text-xs text-zinc-500 text-center"
            data-testid="lobby-start-hint"
          >
            {startTooltip}
          </div>
        )}
      </main>
      <LobbyInviteFriendsModal
        open={inviteFriendsOpen}
        onClose={() => setInviteFriendsOpen(false)}
        lobbyCode={lobby?.lobby_code || code}
        currentMembers={lobby?.players || []}
      />
    </div>
  );
}
